import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

export interface MenuItem {
  id: string;
  name: string;
  name_de: string;
  category: string;
  base_price: number;
  price_with_tax: number;
  description: string;
  description_de: string;
  tax_rate: number;
  is_available: boolean;
  is_popular: boolean;
  sold_out: boolean;
  display_order: number;
  // Per-brand sort override (set from admin Menu page). When present the
  // matching brand's storefront uses this instead of `display_order`.
  display_order_by_brand?: { sushiki?: number; donvito?: number } | null;
  image_url: string;
  brand: ItemBrand; // 'sushiki' | 'donvito' | 'both' — derived if not set in Firestore
  // product_type drives modifier-group auto-attach (2026-05-30, Sadik's spec).
  // e.g. 'pizza' / 'pasta' / 'salad' / 'maki' / 'nigiri' / 'bowl' / 'crispy_roll' / etc.
  product_type?: string;
  modifier_group_ids?: string[];
}

// Modifier types — extended 2026-05-30 for Sadik's option-group spec:
// brand filtering, ingredient-DB size/type pricing matrix, bundle options.
export interface ModifierOption {
  id: string;
  name: string;
  name_de?: string;
  price_delta: number;
  price_delta_by_size?: { [sizeOptionId: string]: number };
  prices_by_size_type?: { [productType: string]: number };
  is_default: boolean;
  is_available: boolean;
  display_order: number;
  is_bundle?: boolean;
  bundle_includes_option_ids?: string[];
  sets_product_type?: string;
}

export interface ModifierGroup {
  id: string;
  name: string;
  name_de?: string;
  type: 'single' | 'multi';
  required: boolean;
  min_select?: number;
  max_select?: number;
  applies_to_size: boolean;
  display_order: number;
  brand?: 'donvito' | 'sushiki' | null;
  applies_to_product_types?: string[];
  default_option_id?: string;
  bundle_options?: Array<{ bundle_option_id: string; contained_option_ids: string[] }>;
  options: ModifierOption[];
}

export type ItemBrand = 'sushiki' | 'donvito' | 'both';

export interface Category {
  id: string;
  name: string;
  icon: string;
  image_url: string;
  is_active: boolean;
  display_order: number;
  display_order_by_brand?: { sushiki?: number; donvito?: number } | null;
}

export interface AddOn {
  id: string;
  name: string;
  name_de: string;
  price: number;
}

// Public brand selectors. 'all' = show everything.
export type Brand = 'sushiki' | 'donvito' | 'all';

/**
 * Normalise any URL slug / user input to a canonical brand.
 *
 * Legacy links used `pizza-don-vito` (from the old Brand type). Current routes use
 * `donvito`. Both need to resolve to the same filter.
 */
export function normalizeBrand(input: unknown): Brand {
  if (!input) return 'all';
  const v = String(input).toLowerCase().trim();
  if (v === 'sushiki' || v === 'sushi' || v === 'sushi-ki') return 'sushiki';
  if (
    v === 'donvito' ||
    v === 'don-vito' ||
    v === 'pizza-don-vito' ||
    v === 'pizza_don_vito' ||
    v === 'pizzadonvito' ||
    v === 'pizza' ||
    v === 'donvitoitalian'
  ) return 'donvito';
  return 'all';
}

// Category-name keywords → brand. Used when an item has no explicit `brand` field
// in Firestore. Order matters: specific cuisines checked before generic buckets.
const BRAND_RULES: { match: RegExp; brand: ItemBrand }[] = [
  // Japanese / sushi
  { match: /(sushi|maki|sashimi|nigiri|temaki|uramaki|onigiri|gunkan|crispy.?roll|inside[- ]?out|hosomaki|futomaki|tempura.?roll|daifuku|mochi|edamame|miso|wakame|dashi|gyoza|ramen|udon|donburi|wasabi|japan|poke|bowl)/i, brand: 'sushiki' },
  // Italian / pizza / pasta
  { match: /(pizza|pizzen|pasta|spaghetti|penne|lasagn|ravioli|gnocchi|tagliatell|fettuccin|linguin|rigatoni|tortellini|risott|calzone|antipasti|antipasto|insalat|bruschett|focacci|panini|tiramisu|dolci|dolce|italien|italian)/i, brand: 'donvito' },
  // Beverages & extras — neutral, show on both
  { match: /(getränk|getraenk|drink|beverage|softdrink|coffee|kaffee|bier|beer|wein|wine|wasser|water|juice|saft|tee|latte|espresso|cola|fanta|sprite|extra|adds?[- ]?on|sauce|dip|beilage|sides?)/i, brand: 'both' },
  // Generic desserts — usually shared unless tagged
  { match: /(dessert|sweet|süss|suess)/i, brand: 'both' },
];

export function deriveBrandFromCategory(category: string): ItemBrand {
  const c = (category || '').trim();
  if (!c) return 'both';
  for (const rule of BRAND_RULES) if (rule.match.test(c)) return rule.brand;
  // Unknown category → default to 'both' so new items never vanish from a domain by accident.
  return 'both';
}

// Cache menu items in memory + sessionStorage (avoids reloading on every page).
// Short TTL so admin toggles (sold_out / display_order) propagate to diners within ~30s.
let _cachedMenuItems: MenuItem[] | null = null;
const MENU_CACHE_KEY = 'qr_menu_items';
// Bumped when brand field was introduced — forces a cache rebuild for returning users.
// v3 (2026-05-30): added product_type field for Sadik's modifier-group auto-attach.
const MENU_CACHE_VERSION = 3;
const MENU_CACHE_TTL = 30 * 1000; // 30 seconds

export async function loadMenuItems(): Promise<MenuItem[]> {
  // Return memory cache instantly
  if (_cachedMenuItems) return _cachedMenuItems;

  // Try sessionStorage cache
  try {
    const cached = sessionStorage.getItem(MENU_CACHE_KEY);
    if (cached) {
      const { items, timestamp, version } = JSON.parse(cached);
      if (version === MENU_CACHE_VERSION && Date.now() - timestamp < MENU_CACHE_TTL && items?.length > 0) {
        _cachedMenuItems = items;
        return items;
      }
    }
  } catch { /* ignore */ }

  // Fetch from Firestore
  const menuItemsRef = collection(db, 'menu_items');
  const snapshot = await getDocs(menuItemsRef);

  const items: MenuItem[] = [];
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    // `is_available === false` permanently hides an item (admin removed it from menu).
    // `sold_out === true` keeps it visible but marks it as temporarily unavailable.
    if (data.is_available === false) return;
    const category = data.category || '';
    // Explicit Firestore `brand` wins; otherwise derive from category name.
    // Admin can override any item later by setting the field — no filter code change needed.
    const explicitBrand = data.brand;
    const brand: ItemBrand =
      explicitBrand === 'sushiki' || explicitBrand === 'donvito' || explicitBrand === 'both'
        ? explicitBrand
        : deriveBrandFromCategory(category);
    items.push({
      id: docSnap.id,
      name: data.name || '',
      name_de: data.name_de || data.name || '',
      category,
      base_price: data.base_price || 0,
      price_with_tax: data.price_with_tax || data.base_price || 0,
      description: data.description || '',
      description_de: data.description_de || data.description || '',
      tax_rate: data.tax_rate || 0,
      is_available: true,
      is_popular: data.is_popular === true,
      sold_out: data.sold_out === true,
      display_order: typeof data.display_order === 'number' ? data.display_order : 999999,
      display_order_by_brand: (data.display_order_by_brand && typeof data.display_order_by_brand === 'object')
        ? { sushiki: data.display_order_by_brand.sushiki, donvito: data.display_order_by_brand.donvito }
        : undefined,
      image_url: data.image_url || '',
      brand,
      product_type: typeof data.product_type === 'string' ? data.product_type : undefined,
      modifier_group_ids: Array.isArray(data.modifier_group_ids) ? data.modifier_group_ids : [],
    });
  });

  // Cache
  _cachedMenuItems = items;
  try {
    sessionStorage.setItem(
      MENU_CACHE_KEY,
      JSON.stringify({ items, timestamp: Date.now(), version: MENU_CACHE_VERSION }),
    );
  } catch { /* storage full */ }

  return items;
}

export async function loadCategories(): Promise<Category[]> {
  const categoriesRef = collection(db, 'categories');
  const snapshot = await getDocs(categoriesRef);

  const categories: Category[] = [];
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.is_active !== false) {
      categories.push({
        id: docSnap.id,
        name: data.name || '',
        icon: data.icon || '',
        image_url: data.image_url || '',
        is_active: data.is_active !== false,
        display_order: data.display_order || 0,
        display_order_by_brand: (data.display_order_by_brand && typeof data.display_order_by_brand === 'object')
          ? { sushiki: data.display_order_by_brand.sushiki, donvito: data.display_order_by_brand.donvito }
          : undefined,
      });
    }
  });

  // Default sort — the caller can re-sort per-brand via sortCategoriesForBrand().
  categories.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  return categories;
}

// Returns the display_order value to use for the given brand. Prefers
// the per-brand slot, falls back to the legacy shared value, then to a
// large sentinel so unsorted items drop to the end.
export function pickDisplayOrderForBrand(
  entity: { display_order?: number; display_order_by_brand?: { sushiki?: number; donvito?: number } | null },
  brand: Brand | string | undefined,
): number {
  const b = normalizeBrand(brand);
  if (b === 'sushiki' || b === 'donvito') {
    const v = entity.display_order_by_brand?.[b];
    if (typeof v === 'number') return v;
  }
  return typeof entity.display_order === 'number' ? entity.display_order : 999999;
}

// Convenience: sort a categories list for a specific brand in-place (or returns new).
export function sortCategoriesForBrand(cats: Category[], brand: Brand | string | undefined): Category[] {
  return [...cats].sort((a, b) => {
    const da = pickDisplayOrderForBrand(a, brand);
    const db = pickDisplayOrderForBrand(b, brand);
    if (da !== db) return da - db;
    return (a.name || '').localeCompare(b.name || '');
  });
}

export async function loadAddonsForItem(menuItemId: string): Promise<AddOn[]> {
  // First: look up menu_item_addons to find linked addon IDs
  const menuItemAddOnsRef = collection(db, 'menu_item_addons');
  const q = query(menuItemAddOnsRef, where('menu_item_id', '==', menuItemId));
  const snapshot = await getDocs(q);

  const addonIds: string[] = [];
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.addon_id) addonIds.push(data.addon_id);
  });

  if (addonIds.length === 0) return [];

  // Fetch each addon by ID
  const addons: AddOn[] = [];
  for (const addonId of addonIds) {
    try {
      const addonDocRef = doc(db, 'addons', addonId);
      const addonDocSnap = await getDoc(addonDocRef);
      if (addonDocSnap.exists()) {
        const data = addonDocSnap.data();
        if (data.is_active !== false) {
          addons.push({
            id: addonDocSnap.id,
            name: data.name || '',
            name_de: data.name_de || data.name || '',
            price: parseFloat(data.price || '0'),
          });
        }
      }
    } catch (err) {
      console.warn(`Could not fetch addon ${addonId}:`, err);
    }
  }

  return addons;
}

/**
 * Group menu items by category, filtered to the given brand.
 *
 * Brand rules:
 * - 'sushiki'  → keep items where brand === 'sushiki' or brand === 'both'
 * - 'donvito'  → keep items where brand === 'donvito' or brand === 'both'
 * - 'all' / undefined → keep everything (default customer site, table QR / dine-in flow
 *   after brand selection page picks one, and any unknown value)
 *
 * A category that ends up empty after filtering is dropped (avoids an empty
 * "Pizza" pill on the SushiKi domain).
 */
// Helper exported so callers can dynamically reprice options when a Pizza
// Size selection upgrades the host product_type at runtime.
export const productTypeMatches = (groupTypes: string[] | undefined, productType?: string): boolean => {
  if (!productType || !Array.isArray(groupTypes)) return false;
  if (groupTypes.includes(productType)) return true;
  if (productType === 'pizza' && groupTypes.some((t) => t.startsWith('pizza_'))) return true;
  return false;
};

export async function loadModifierGroupsFor(item: MenuItem): Promise<ModifierGroup[]> {
  // Two attachment sources: explicit (legacy) and auto-match by brand +
  // product_type so Sadik's "Pizza Dips" group applies to every Don Vito pizza
  // without manual linkage on each product.
  const explicitIds: string[] = Array.isArray(item.modifier_group_ids) ? item.modifier_group_ids : [];
  const productBrand = (item as any).brand as string | null | undefined;
  const productType = (item as any).product_type as string | undefined;

  const seen = new Set<string>();
  const groupDocs: Array<{ id: string; data: any }> = [];

  for (const gid of explicitIds) {
    try {
      const gSnap = await getDoc(doc(db, 'modifier_groups', gid));
      if (gSnap.exists() && !seen.has(gSnap.id)) {
        seen.add(gSnap.id);
        groupDocs.push({ id: gSnap.id, data: gSnap.data() });
      }
    } catch {}
  }
  if (productType) {
    try {
      const allG = await getDocs(collection(db, 'modifier_groups'));
      allG.forEach((gd) => {
        if (seen.has(gd.id)) return;
        const g = gd.data() as any;
        if (g.is_active === false) return;
        const brandOk = !g.brand || !productBrand || g.brand === productBrand || productBrand === 'both';
        const typesOk = productTypeMatches(g.applies_to_product_types, productType);
        if (brandOk && typesOk) {
          seen.add(gd.id);
          groupDocs.push({ id: gd.id, data: g });
        }
      });
    } catch {}
  }
  if (groupDocs.length === 0) return [];

  const groups: ModifierGroup[] = [];
  for (const gd of groupDocs) {
    try {
      const g = gd.data;
      if (g.is_active === false) continue;
      const optsSnap = await getDocs(query(collection(db, 'modifier_options'), where('group_id', '==', gd.id)));
      const options: ModifierOption[] = [];
      optsSnap.forEach((od) => {
        const o = od.data() as any;
        if (o.is_available === false) return;
        options.push({
          id: od.id,
          name: o.name || '',
          name_de: o.name_de || '',
          price_delta: parseFloat(o.price_delta || 0),
          price_delta_by_size: o.price_delta_by_size || undefined,
          prices_by_size_type: o.prices_by_size_type || undefined,
          is_default: !!o.is_default,
          is_available: o.is_available !== false,
          display_order: o.display_order || 0,
          is_bundle: !!o.is_bundle,
          bundle_includes_option_ids: o.bundle_includes_option_ids || undefined,
          sets_product_type: o.sets_product_type || undefined,
        });
      });
      options.sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name));
      groups.push({
        id: gd.id,
        name: g.name || '',
        name_de: g.name_de || '',
        type: g.type === 'multi' ? 'multi' : 'single',
        required: !!g.required,
        min_select: g.min_select,
        max_select: g.max_select,
        applies_to_size: !!g.applies_to_size,
        display_order: g.display_order || 0,
        brand: g.brand ?? null,
        applies_to_product_types: g.applies_to_product_types || undefined,
        default_option_id: g.default_option_id || undefined,
        bundle_options: g.bundle_options || undefined,
        options,
      });
    } catch (err) {
      console.warn('[QR] Failed to load modifier group', gd.id, err);
    }
  }
  groups.sort((a, b) => a.display_order - b.display_order);
  return groups;
}

export function getMenuItemsByBrand(items: MenuItem[], brand?: Brand | string): Map<string, MenuItem[]> {
  const grouped = new Map<string, MenuItem[]>();
  // Accept legacy slugs ('pizza-don-vito', etc.) without breaking old links.
  const target = normalizeBrand(brand);

  const keep = (item: MenuItem): boolean => {
    if (!item.is_available) return false;
    if (target === 'all') return true;
    if (item.brand === 'both') return true;
    return item.brand === target;
  };

  for (const item of items) {
    if (!keep(item)) continue;
    const list = grouped.get(item.category) || [];
    list.push(item);
    grouped.set(item.category, list);
  }

  // Sort items within each category by admin-set per-brand display order.
  // Falls back to the shared `display_order` when no per-brand slot is set —
  // ensures existing menus keep their current arrangement until the admin
  // reorders explicitly for that brand.
  grouped.forEach((list) => {
    list.sort((a, b) => {
      const da = pickDisplayOrderForBrand(a, target);
      const db = pickDisplayOrderForBrand(b, target);
      if (da !== db) return da - db;
      return a.name.localeCompare(b.name);
    });
  });

  // Category order is determined by loadCategories() (sorted by display_order there).
  // We can't reorder this map by category display_order without it — so leave alphabetical here
  // as a fallback; the MenuPage renders categories in the order returned by loadCategories() anyway.
  const keys = Array.from(grouped.keys()).sort((a, b) => a.localeCompare(b));
  const sorted = new Map<string, MenuItem[]>();
  for (const key of keys) {
    sorted.set(key, grouped.get(key)!);
  }
  return sorted;
}
